/**
 * CRM deep-link helpers.
 *
 * Companies (US-CMP-02): `VITE_CRM_BASE_URL` is the account entity form (no
 * record id). `crmGuid` is appended as `id=`.
 *
 * Properties / Vault Admin (FR-73): the table holds a Dynamics record GUID.
 * The button concatenates `VITE_CRM_PROPERTY_BASE_URL` (or `VITE_CRM_BASE_URL`
 * with `etn=new_property`) + that GUID. Leftover migrated full URLs are parsed
 * for `id=` so Open in CRM still works before the backfill lands.
 */

const CRM_RECORD_ID_PARAM = 'id';
const PROPERTY_ENTITY_NAME = 'new_property';
const GUID_RE =
  /^\{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\}?$/;

/**
 * Pulls a Dynamics record GUID from a bare id or a migrated `main.aspx` URL.
 */
export function extractCrmRecordId(stored: string | null | undefined): string | null {
  if (!stored) return null;
  const raw = stored.trim();
  if (!raw) return null;

  const asGuid = normalizeCrmGuid(raw);
  if (asGuid) return asGuid;

  try {
    if (!raw.includes('://')) return null;
    const url = new URL(raw);
    const id = url.searchParams.get(CRM_RECORD_ID_PARAM);
    return id ? normalizeCrmGuid(id) : null;
  } catch {
    return null;
  }
}

function normalizeCrmGuid(value: string): string | null {
  const trimmed = value.trim().replace(/^\{|\}$/g, '');
  return GUID_RE.test(trimmed) ? trimmed : null;
}

function appendCrmRecordId(base: string, recordId: string): string | null {
  try {
    const url = base.includes('://')
      ? new URL(base)
      : new URL(base, window.location.origin);
    url.searchParams.set(CRM_RECORD_ID_PARAM, recordId);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Builds an "Open CRM" URL for a company account record, or null when the
 * base URL or CRM guid is missing.
 */
export function buildOpenCrmCompanyUrl(crmGuid: string | null | undefined): string | null {
  const base = import.meta.env.VITE_CRM_BASE_URL?.trim();
  if (!base || !crmGuid?.trim()) return null;
  return appendCrmRecordId(base, crmGuid.trim());
}

/**
 * Dynamics property-form base for this environment (no record id).
 */
export function getPropertyCrmBaseUrl(): string | null {
  const dedicated = import.meta.env.VITE_CRM_PROPERTY_BASE_URL?.trim();
  if (dedicated) return dedicated;

  const company = import.meta.env.VITE_CRM_BASE_URL?.trim();
  if (!company) return null;

  try {
    const url = company.includes('://')
      ? new URL(company)
      : new URL(company, window.location.origin);
    url.searchParams.set('etn', PROPERTY_ENTITY_NAME);
    url.searchParams.delete(CRM_RECORD_ID_PARAM);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Builds Overview / Vault Admin "Open in CRM" for a property.
 *
 * `stored` may be a GUID or a full URL copied from another environment.
 * The host always comes from env, never from the stored string.
 */
export function buildOpenCrmPropertyUrl(stored: string | null | undefined): string | null {
  const base = getPropertyCrmBaseUrl();
  const recordId = extractCrmRecordId(stored);
  if (!base || !recordId) return null;
  return appendCrmRecordId(base, recordId);
}

/**
 * Explains why the Open CRM action is disabled — distinguishes missing env
 * configuration from a company that lacks a CRM record id.
 */
export function getOpenCrmDisabledReason(crmGuid: string | null | undefined): string | null {
  const base = import.meta.env.VITE_CRM_BASE_URL?.trim();
  if (!base) {
    return 'CRM link unavailable — set VITE_CRM_BASE_URL in your environment.';
  }
  if (!crmGuid?.trim()) {
    return 'CRM link unavailable — this company has no CRM record ID.';
  }
  return null;
}
